/** Wirft bei Konflikt einen KonfliktError mit den betroffenen Belegungen. */
export class KonfliktError extends Error {
    constructor(message, konflikte) {
        super(message);
        this.name = 'KonfliktError';
        this.konflikte = konflikte;
    }
}
/** Wirft beim Löschen eines belegten Platzes / Teams (HTTP 409). */
export class PlatzBelegtError extends Error {
    constructor(message, anzahl) {
        super(message);
        this.name = 'PlatzBelegtError';
        this.anzahl = anzahl;
    }
}
/** Wirft, wenn eine Belegung durch eine Sperre blockiert wird (HTTP 409). */
export class SperreError extends Error {
    constructor(message, sperre) {
        super(message);
        this.name = 'SperreError';
        this.sperre = sperre;
    }
}
/** Wirft, wenn eine Belegung außerhalb der Öffnungszeit liegt (HTTP 409). */
export class OeffnungError extends Error {
    constructor(message, von, bis) {
        super(message);
        this.name = 'OeffnungError';
        this.von = von;
        this.bis = bis;
    }
}
function config() {
    const c = window.MPPlatzbelegung;
    if (!c) {
        throw new Error('MPPlatzbelegung-Konfiguration fehlt.');
    }
    return c;
}
async function request(pfad, init = {}) {
    const { restUrl, nonce } = config();
    const res = await fetch(restUrl + pfad, {
        ...init,
        headers: {
            'Content-Type': 'application/json',
            'X-WP-Nonce': nonce,
            ...(init.headers || {}),
        },
    });
    const data = await res.json().catch(() => ({}));
    if (res.status === 409 && data?.data?.konflikte) {
        throw new KonfliktError(data.message || 'Konflikt', data.data.konflikte);
    }
    if (res.status === 409 && typeof data?.data?.belegungen === 'number') {
        throw new PlatzBelegtError(data.message || 'Platz belegt', data.data.belegungen);
    }
    if (res.status === 409 && data?.data?.sperre) {
        throw new SperreError(data.message || 'Platz gesperrt', data.data.sperre);
    }
    if (res.status === 409 && data?.data?.oeffnungszeit) {
        const o = data.data.oeffnungszeit;
        throw new OeffnungError(data.message || 'Außerhalb der Öffnungszeit', o.von, o.bis);
    }
    if (!res.ok) {
        throw new Error(data?.message || `Fehler ${res.status}`);
    }
    return data;
}
export const api = {
    getPlan: () => request('/plan'),
    createBelegung: (b, force = false) => request(`/belegungen${force ? '?force=1' : ''}`, {
        method: 'POST',
        body: JSON.stringify(b),
    }),
    updateBelegung: (id, b, force = false) => request(`/belegungen/${id}${force ? '?force=1' : ''}`, {
        method: 'PUT',
        body: JSON.stringify(b),
    }),
    deleteBelegung: (id) => request(`/belegungen/${id}`, { method: 'DELETE' }),
    checkKonflikte: (b) => request('/konflikte', {
        method: 'POST',
        body: JSON.stringify(b),
    }),
    createPlatz: (p) => request('/plaetze', { method: 'POST', body: JSON.stringify(p) }),
    updatePlatz: (id, p) => request(`/plaetze/${id}`, { method: 'PUT', body: JSON.stringify(p) }),
    deletePlatz: (id, force = false) => request(`/plaetze/${id}${force ? '?force=1' : ''}`, { method: 'DELETE' }),
    // Teams: Anlegen/Bearbeiten/Löschen erfolgt zentral über die
    // Kern-Seite „Mannschaften"; hier nur noch Lesen (via /plan).
    saveAusnahme: (a) => request('/ausnahmen', { method: 'POST', body: JSON.stringify(a) }),
    deleteAusnahme: (id) => request(`/ausnahmen/${id}`, { method: 'DELETE' }),
    getSettings: () => request('/settings'),
    updateSettings: (s) => request('/settings', { method: 'POST', body: JSON.stringify(s) }),
    saveSaison: (s) => request(s.id ? `/saisons/${s.id}` : '/saisons', {
        method: s.id ? 'PUT' : 'POST',
        body: JSON.stringify(s),
    }),
    deleteSaison: (id) => request(`/saisons/${id}`, { method: 'DELETE' }),
    setSaisonAktiv: (id) => request(`/saisons/${id}/aktiv`, { method: 'POST' }),
    regenerateFeed: () => request('/feed/token', { method: 'POST' }),
    saveSchliesszeit: (s) => request(s.id ? `/schliesszeiten/${s.id}` : '/schliesszeiten', {
        method: s.id ? 'PUT' : 'POST',
        body: JSON.stringify(s),
    }),
    deleteSchliesszeit: (id) => request(`/schliesszeiten/${id}`, { method: 'DELETE' }),
    importFeiertage: (jahr) => request('/schliesszeiten/import', { method: 'POST', body: JSON.stringify({ jahr }) }),
    setBelegungStatus: (id, status) => request(`/belegungen/${id}/status`, {
        method: 'POST',
        body: JSON.stringify({ status }),
    }),
    createSperre: (s) => request('/sperren', { method: 'POST', body: JSON.stringify(s) }),
    updateSperre: (id, s) => request(`/sperren/${id}`, { method: 'PUT', body: JSON.stringify(s) }),
    deleteSperre: (id) => request(`/sperren/${id}`, { method: 'DELETE' }),
};
